import { useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { AppDialog } from "../../components/AppDialog";
import { StateDialog, type StateRow } from "./StateDialog";
import { LinkPickerDialog, type LinkOption } from "./LinkPickerDialog";

interface Option {
  id: string;
  name: string;
}

interface Props {
  /** POST endpoint that creates the country (/api/v1/system/countries). */
  createUrl: string;
  /** The parent Country select's optionsUrl — re-read after create to pick up the fresh
   *  {id, name} and select it. */
  optionsUrl: string;
  onClose: () => void;
  onAdded: (options: Option[], selectedId: string) => void;
}

/** Strip the `data:<mime>;base64,` prefix — the API expects raw base64 for NationalFlag. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/**
 * The legacy CRM "Country" master popup (Unfollowed Clients ▸ Country "+", owner request
 * 2026-10-23) — the TOP of the Country ▸ State ▸ City ▸ Area nested master (images 835-846). English
 * Long Name* + English Short Name + National Flag (image) + a State child grid with the legacy
 * toolbar New · Link · Unlink · Edit · Export to. State New / Edit open the {@link StateDialog}; Link
 * opens the shared {@link LinkPickerDialog} (search + checkboxes + edit + pagination).
 *
 * On OK it creates the country, then cascades the buffered tree: each state (POST new / PUT linked),
 * then each of its cities (POST / PUT, under the city's re-assigned state or the cascade parent),
 * then each of that city's areas (POST new area + link via dbo.City_Areas). Finally it re-reads the
 * parent Country options and selects the new row.
 */
export function CountryDialog({ createUrl, optionsUrl, onClose, onAdded }: Props) {
  const notify = useNotify();
  const [longName, setLongName] = useState("");
  const [shortName, setShortName] = useState("");
  const [flagFile, setFlagFile] = useState<File | null>(null);
  const [states, setStates] = useState<StateRow[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  // Nested State popup (New / Edit / edit-from-link). `stateIdx` is the buffer row being replaced
  // (null = add a new/linked row); `stateInitial` is what the popup opens with.
  const [stateOpen, setStateOpen] = useState(false);
  const [stateIdx, setStateIdx] = useState<number | null>(null);
  const [stateInitial, setStateInitial] = useState<StateRow | undefined>(undefined);

  // State Link picker candidates (existing states not yet on a country). `statePool` keeps the FULL
  // fetched records so Link / edit-pencil can preload a state's own ShortName + Country.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkOptions, setLinkOptions] = useState<LinkOption[]>([]);
  const [statePool, setStatePool] = useState<{ id: string; longName?: string; shortName?: string; countryId?: string | null }[]>([]);

  const addState = () => {
    setStateIdx(null);
    setStateInitial(undefined);
    setStateOpen(true);
  };
  const editState = () => {
    if (sel == null) return;
    setStateIdx(sel);
    setStateInitial(states[sel]);
    setStateOpen(true);
  };
  const unlink = () => {
    if (sel == null) return;
    setStates((s) => s.filter((_, j) => j !== sel));
    setSel(null);
  };
  const onStateOk = (row: StateRow) => {
    setStates((s) => (stateIdx == null ? [...s, row] : s.map((r, j) => (j === stateIdx ? row : r))));
    setStateOpen(false);
  };

  const openLink = async () => {
    try {
      const { data } = await api.get<{
        data?: { id: string; longName?: string; shortName?: string; countryId?: string | null }[];
      }>("/api/v1/system/states", { silentLoading: true } as never);
      // Legacy's "Link" shows EVERY state not already in THIS country's grid (it re-parents a linked
      // one to this country on save), so a state that belongs to another country still appears —
      // matching the old form (image 847, where CA/CO/TX show even though they are under US).
      const have = new Set(states.map((r) => r.oid).filter(Boolean));
      const pool = (data?.data ?? []).filter((r) => !have.has(r.id));
      setStatePool(pool);
      setLinkOptions(pool.map((r) => ({ id: r.id, label: r.longName ?? "" })));
      setLinkOpen(true);
    } catch {
      /* axios error dialog already surfaced it */
    }
  };
  // Link preserves the state's OWN short name (so the re-parenting PUT does not blank it); countryOid
  // is left empty so the cascade re-parents it to the new country.
  const confirmLink = (picked: LinkOption[]) => {
    setStates((s) => [
      ...s,
      ...picked.map((p): StateRow => {
        const full = statePool.find((x) => x.id === p.id);
        return { oid: p.id, longName: p.label, shortName: full?.shortName ?? "", cities: [] };
      }),
    ]);
    setLinkOpen(false);
  };
  // Edit-from-link: open the State popup on an existing candidate with its OWN short name + country
  // preloaded (legacy image 851 shows "CA" + "United States"), then link/keep it on OK.
  const editLinkState = (opt: LinkOption) => {
    const full = statePool.find((x) => x.id === opt.id);
    setLinkOpen(false);
    setStateIdx(null);
    setStateInitial({ oid: opt.id, longName: opt.label, shortName: full?.shortName ?? "", countryOid: full?.countryId ?? undefined, cities: [] });
    setStateOpen(true);
  };

  const exportStates = () => {
    const header = "State,ShortName";
    const body = states.map((r) => `"${r.longName}","${r.shortName}"`).join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${longName.trim() || "country"}-states.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const submit = async () => {
    setTried(true);
    if (!longName.trim()) return;
    setSaving(true);
    try {
      const nationalFlag = flagFile ? await fileToBase64(flagFile) : undefined;
      const created = await api.post<{ data?: { id?: string } }>(createUrl, {
        longName: longName.trim(),
        shortName: shortName.trim() || undefined,
        nationalFlag,
      });
      const countryId = created.data?.data?.id ?? "";
      const stateRows = states.filter((r) => r.longName.trim());
      if (countryId && stateRows.length) {
        // Country → State → City → Area cascade: each level first (so its id exists), then its children.
        for (const st of stateRows) {
          // The state sits under its re-assigned country (combo) if set, else the new country.
          const statePayload = { longName: st.longName.trim(), shortName: st.shortName.trim() || undefined, countryId: st.countryOid || countryId };
          const stRes = st.oid
            ? await api.put<{ data?: { id?: string } }>(`/api/v1/system/states/${st.oid}`, statePayload)
            : await api.post<{ data?: { id?: string } }>("/api/v1/system/states", statePayload);
          const stateId = st.oid ?? stRes.data?.data?.id ?? "";
          for (const c of st.cities.filter((x) => x.cityName.trim())) {
            // The city sits under its re-assigned state (combo) if set, else the cascade parent.
            const cityPayload = { cityName: c.cityName.trim(), stateId: c.stateOid || stateId };
            const cRes = c.oid
              ? await api.put<{ data?: { id?: string } }>(`/api/v1/system/cities/${c.oid}`, cityPayload)
              : await api.post<{ data?: { id?: string } }>("/api/v1/system/cities", cityPayload);
            const cityId = c.oid ?? cRes.data?.data?.id ?? "";
            for (const ar of c.areas.filter((x) => x.areaName.trim())) {
              const areaId = ar.oid ?? (await api.post<{ data?: { id?: string } }>("/api/v1/system/areas", { areaName: ar.areaName.trim() })).data?.data?.id ?? "";
              if (cityId && areaId) await api.post(`/api/v1/system/cities/${cityId}/areas/${areaId}/link`);
            }
          }
        }
      }
      const { data } = await api.get<{ data?: Option[] }>(optionsUrl, { silentLoading: true } as never);
      const fresh = data?.data ?? [];
      const selected =
        (countryId && fresh.find((o) => o.id.toLowerCase() === countryId.toLowerCase())?.id) ||
        fresh.find((o) => o.name === longName.trim())?.id ||
        countryId;
      notify.success("Saved successfully.");
      onAdded(fresh, selected);
      onClose();
    } catch {
      /* axios error dialog already surfaced it */
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open
      title="Country"
      icon="ti-world"
      width={560}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={submit} disabled={saving}>
            OK
          </button>
        </>
      }
    >
      <div className="cd-form">
        <div className="cd-field">
          <label className="cd-label" htmlFor="cd-long">
            English Long Name:<span className="req">*</span>
          </label>
          <input
            id="cd-long"
            className={`op-input${tried && !longName.trim() ? " required-empty" : ""}`}
            value={longName}
            onChange={(e) => setLongName(e.target.value)}
          />
        </div>
        <div className="cd-field">
          <label className="cd-label" htmlFor="cd-short">
            English Short Name:
          </label>
          <input id="cd-short" className="op-input" value={shortName} onChange={(e) => setShortName(e.target.value)} />
        </div>
        <div className="cd-field">
          <label className="cd-label" htmlFor="cd-flag">
            National Flag:
          </label>
          <input
            id="cd-flag"
            className="op-input op-file"
            type="file"
            accept="image/*"
            onChange={(e) => setFlagFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className="cd-state">
          <div className="cd-state-h">State</div>
          <div className="op-childbar">
            <button type="button" className="op-cb" onClick={addState} disabled={saving}>
              <i className="ti ti-plus" aria-hidden="true" /> New
            </button>
            <button type="button" className="op-cb" onClick={openLink} disabled={saving}>
              <i className="ti ti-link" aria-hidden="true" /> Link
            </button>
            <button type="button" className={`op-cb${sel == null ? " muted" : ""}`} onClick={unlink} disabled={saving || sel == null}>
              <i className="ti ti-unlink" aria-hidden="true" /> Unlink
            </button>
            <button type="button" className={`op-cb${sel == null ? " muted" : ""}`} onClick={editState} disabled={saving || sel == null}>
              <i className="ti ti-pencil" aria-hidden="true" /> Edit
            </button>
            <button type="button" className={`op-cb${states.length === 0 ? " muted" : ""}`} onClick={exportStates} disabled={states.length === 0}>
              <i className="ti ti-file-export" aria-hidden="true" /> Export to
            </button>
          </div>
          <table className="cd-state-tbl">
            <thead>
              <tr>
                <th className="cd-sel" aria-label="select" />
                <th className="cd-edit" aria-label="edit" />
                <th>State</th>
                <th>ShortName</th>
              </tr>
            </thead>
            <tbody>
              {states.length === 0 ? (
                <tr>
                  <td colSpan={4} className="cd-empty">
                    No data to display
                  </td>
                </tr>
              ) : (
                states.map((r, i) => (
                  <tr key={i} className={sel === i ? "cd-rowsel" : undefined} onClick={() => setSel(i)}>
                    <td className="cd-sel">
                      <input type="checkbox" checked={sel === i} onChange={() => setSel(sel === i ? null : i)} />
                    </td>
                    <td className="cd-edit">
                      <button
                        type="button"
                        className="sd-link-edit"
                        title="Edit"
                        aria-label="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStateIdx(i);
                          setStateInitial(states[i]);
                          setStateOpen(true);
                        }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                    </td>
                    <td>{r.longName}</td>
                    <td>{r.shortName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {stateOpen && (
        <StateDialog initial={stateInitial} parentCountryLabel={longName} onOk={onStateOk} onClose={() => setStateOpen(false)} />
      )}

      {linkOpen && (
        <LinkPickerDialog
          title="Link State"
          options={linkOptions}
          columnLabel="State"
          emptyText="No states available to link"
          onEdit={editLinkState}
          onOk={confirmLink}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </AppDialog>
  );
}
